import { Wallet } from "@coral-xyz/anchor";
import {
  ISupportedWallet,
  ModuleInterface,
  StellarWalletsKit,
  WalletNetwork,
  allowAllModules,
} from "@creit.tech/stellar-wallets-kit";
import HotWalletModule, { HOTWALLET_ID } from "@hot-wallet/sdk/adapter/stellar";
import { useAnchorWallet, useConnection } from "@solana/wallet-adapter-react";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { t } from "i18next";
import { mutate } from "swr";
import { cache } from "swr/_internal";

import { ToastService } from "@/components/ToastService";
import { CHAIN_BY_NETWORK } from "@/constants/chains";
import { API_ROUTES } from "@/interfaces/api";
import { Network } from "@/interfaces/network";
import { useModalContext } from "@/providers/modal-provider/context";
import { useWalletSelector } from "@/providers/near-provider";
import { evm, near, solana, ton } from "@/services/helpers";
import stellar from "@/services/helpers/stellar";
import { ApiVersion, apiVersion, delay, hexToBytes } from "@/utils";

import { useStore } from "./store";

export function clearCache() {
  const keys = cache.keys();
  for (const key of keys) {
    cache.delete(key);
  }
}

const kit: StellarWalletsKit = new StellarWalletsKit({
  network: WalletNetwork.PUBLIC,
  selectedWalletId: HOTWALLET_ID,
  modules: [new HotWalletModule() as ModuleInterface, ...allowAllModules()],
});

export const useNetworkDependentActions = (): {
  mint: () => Promise<boolean>;
  reMint: () => Promise<boolean>;
  mintNFT: () => Promise<boolean>;
  claimAirdrop: (airdropId: number, nftIds: Array<string>) => Promise<boolean>;
  disconnect?: () => void;
  connectNetwork?: () => Promise<void>;
} => {
  const { network, user, currentNetworkData, eraseUserData, manualScoreUpdate, updateAccounts } = useStore((store) => ({
    network: store.network,
    user: store.user,
    updateAccounts: store.updateAccounts,
    currentNetworkData: store.user.networksData[store.network],
    eraseUserData: store.eraseUserData,
    manualScoreUpdate: store.manualScoreUpdate,
  }));
  const { selector } = useWalletSelector();

  const wallet = useAnchorWallet();
  const { connection } = useConnection();
  const modal = useModalContext();

  const [tonConnectUI] = useTonConnectUI();
  const isWaitingMint = () => {
    if (user.isWaitingMint) {
      ToastService.simple({ text: t("Messages.WaitingMint") });
      return true;
    }
    return false;
  };

  return {
    ...(() => {
      switch (network) {
        case Network.NEAR: {
          return {
            mint: async () => {
              if (!selector || !user.nearAccount) return false;
              await near.mintScore({ selector, address: user.nearAccount });
              return true;
            },
            reMint: async () => {
              if (isWaitingMint() || !selector || !user.nearAccount) return false;
              await near.mintScore({ selector, address: user.nearAccount, isUpdateMint: true });
              return true;
            },
            mintNFT: async () => {
              if (!selector || !user.nearAccount || !currentNetworkData.nft?.mintingFee) return false;
              return near.mintNft({
                selector,
                address: user.nearAccount,
                fee: currentNetworkData.nft.mintingFee,
                openModal: modal.nftPurchased.openModal,
              });
            },
            claimAirdrop: async (airdropId, nftIds) => {
              if (!selector || !user.nearAccount) return false;
              return near.claimAirdrop({
                selector,
                address: user.nearAccount,
                nftIds,
                airdropId,
                openModal: modal.airdropClaimed.openModal,
              });
            },
            disconnect: async () => {
              eraseUserData();
              mutate(
                `${API_ROUTES.TRUST}/${network}/${apiVersion === ApiVersion.v1 ? user.nearAccount : user.tonAccount}`,
              );
              clearCache();
              const wallet = await selector.wallet();
              wallet.signOut();
            },
          };
        }

        case Network.TON:
        case Network.TON_TESTNET: {
          const manualUpdate = (data: { trustScore: bigint; expirationDate: bigint }) => {
            manualScoreUpdate(network, Number(data.trustScore), Number(data.expirationDate) * 1000);
          };

          return {
            mint: async () => {
              const retrievedScores = currentNetworkData.retrievedScores;
              if (isWaitingMint() || !user.tonAccount || !retrievedScores) return false;
              return ton.mintScore({
                address: user.tonAccount,
                tonConnectUI,
                signature: retrievedScores.signature,
                trustScore: retrievedScores.trust,
                expirationDate: retrievedScores.expiration,
                manualUpdate,
              });
            },
            reMint: async () => {
              const retrievedScores = currentNetworkData.retrievedScores;
              if (isWaitingMint() || !user.tonAccount || !retrievedScores) return false;
              return ton.mintScore({
                address: user.tonAccount,
                tonConnectUI,
                signature: retrievedScores.signature,
                trustScore: retrievedScores.trust,
                expirationDate: retrievedScores.expiration,
                manualUpdate,
                isUpdateMint: true,
              });
            },
            mintNFT: async () => {
              return false;
            },
            claimAirdrop: async () => {
              return false;
            },
            disconnect: () => {
              eraseUserData();
              mutate(
                `${API_ROUTES.TRUST}/${network}/${apiVersion === ApiVersion.v1 ? user.nearAccount : user.tonAccount}`,
              );
              clearCache();
              tonConnectUI.disconnect();
            },
          };
        }

        case Network.SOLANA: {
          const manualUpdate = (data: { trustScore: number; expirationDate: number }) => {
            manualScoreUpdate(network, Number(data.trustScore), data.expirationDate);
          };

          return {
            mint: async () => {
              if (isWaitingMint() || !user.solanaAccount) return false;
              const retrievedScores = currentNetworkData.retrievedScores;
              return solana.mintScore({
                address: user.solanaAccount,
                wallet: wallet as Wallet,
                connection,
                retrievedScores,
                manualUpdate,
              });
            },
            reMint: async () => {
              if (isWaitingMint() || !user.solanaAccount) return false;
              const retrievedScores = currentNetworkData.retrievedScores;

              return solana.mintScore({
                address: user.solanaAccount,
                wallet: wallet as Wallet,
                connection,
                retrievedScores,
                manualUpdate,
                isUpdateMint: true,
              });
            },
            mintNFT: async () => {
              return false;
            },
            claimAirdrop: async () => {
              return false;
            },
          };
        }

        case Network.STELLAR: {
          return {
            mint: async () => {
              const retrievedScores = currentNetworkData.retrievedScores;
              if (!retrievedScores || !user.stellarAccount) return false;
              return await stellar.mintScore({
                message: Buffer.from(`${retrievedScores.wallet},${retrievedScores.trust}`),
                signature: Buffer.from(hexToBytes(retrievedScores.signature)),
                address: user.stellarAccount,
              });
            },
            reMint: async () => {
              const retrievedScores = currentNetworkData.retrievedScores;
              if (!retrievedScores || !user.stellarAccount) return false;

              return await stellar.mintScore({
                message: Buffer.from(`${retrievedScores.wallet},${retrievedScores.trust}`),
                signature: Buffer.from(hexToBytes(retrievedScores.signature)),
                address: user.stellarAccount,
              });
            },
            mintNFT: async () => {
              return false;
            },
            claimAirdrop: async () => {
              return false;
            },

            connectNetwork: async () => {
              await kit.openModal({
                onWalletSelected: async (option: ISupportedWallet) => {
                  kit.setWallet(option.id);
                  const { address } = await kit.getAddress();
                  updateAccounts({ stellarAccount: address });
                  await delay(100);
                  mutate(`${API_ROUTES.USER}/${user.nearAccount}`);
                  mutate(
                    `${API_ROUTES.TRUST}/${network}/${apiVersion === ApiVersion.v1 ? user.nearAccount : user.tonAccount}`,
                  );
                  return address;
                },
              });
            },
          };
        }
        case Network.ETHEREUM:
          return {
            mint: async () => {
              const requiredChain = CHAIN_BY_NETWORK[network];
              const retrievedScores = currentNetworkData.retrievedScores;

              if (
                !requiredChain ||
                isWaitingMint() ||
                !user.evmAccount ||
                !retrievedScores ||
                !retrievedScores.validation
              )
                return false;

              return evm.mintScoreNative({
                address: user.evmAccount,
                chainId: requiredChain,
                trust: retrievedScores.trust,
                expirationDate: retrievedScores.expiration,
                validation: retrievedScores.validation,
                isReMint: Boolean(retrievedScores.isRemint),
              });
            },
            reMint: async () => {
              const requiredChain = CHAIN_BY_NETWORK[network];
              const retrievedScores = currentNetworkData.retrievedScores;

              if (
                !requiredChain ||
                isWaitingMint() ||
                !user.evmAccount ||
                !retrievedScores ||
                !retrievedScores.validation
              )
                return false;
              return evm.mintScoreNative({
                address: user.evmAccount,
                chainId: requiredChain,
                attestationId: retrievedScores.attestationId,
                trust: retrievedScores.trust,
                expirationDate: retrievedScores.expiration,
                validation: retrievedScores.validation,
                isReMint: Boolean(retrievedScores.isRemint),
              });
            },
            mintNFT: async () => {
              return false;
            },
            claimAirdrop: async () => {
              return false;
            },
          };
        default:
          return {
            mint: async () => {
              const requiredChain = CHAIN_BY_NETWORK[network];
              const retrievedScores = currentNetworkData.retrievedScores;
              if (
                !requiredChain ||
                isWaitingMint() ||
                !user.evmAccount ||
                !retrievedScores ||
                !retrievedScores.validation
              )
                return false;
              return evm.mintScore({
                address: user.evmAccount,
                chainId: requiredChain,
                trust: retrievedScores.trust,
                expirationDate: retrievedScores.expiration,
                validation: retrievedScores.validation,
                isReMint: Boolean(retrievedScores.isRemint),
              });
            },
            reMint: async () => {
              const requiredChain = CHAIN_BY_NETWORK[network];
              const retrievedScores = currentNetworkData.retrievedScores;

              if (
                !requiredChain ||
                isWaitingMint() ||
                !user.evmAccount ||
                !retrievedScores ||
                !retrievedScores.validation
              )
                return false;
              return evm.mintScore({
                address: user.evmAccount,
                chainId: requiredChain,
                attestationId: retrievedScores.attestationId,
                trust: retrievedScores.trust,
                expirationDate: retrievedScores.expiration,
                validation: retrievedScores.validation,
                isReMint: Boolean(retrievedScores.isRemint),
              });
            },
            mintNFT: async () => {
              return false;
            },
            claimAirdrop: async () => {
              return false;
            },
          };
      }
    })(),
  };
};
